import { useFlagsFromDualInstances } from '@linktr.ee/feature-flags-mfe/DualStatsigInstances';
import { useEffect, useRef } from 'react';
import { addClientTiming } from '@shared/utils/clientMonitoring';
import { isMobileApp } from '@shared/utils/descopeQueryParams';
import { isEmbeddedBrowser } from '@shared/utils/detectDevice';
import { reportClientError } from '@shared/utils/reportClientError';
import { browserSupportsWebAuthn } from './passkeys';
import { ScreenNames } from './screenMapping';

export const PASSKEY_LOGIN_GATE = 'passkeys_login';
export const PASSKEY_LOGIN_GATE_READY_TIMING =
  'passkey_login_gate_ready_at_login_screen';
export const PASSKEY_LOGIN_GATE_NOT_READY_TIMING =
  'passkey_login_gate_not_ready_at_login_screen';
export const PASSKEY_LOGIN_ENABLED_FORM_KEY =
  'form.passkeysLoginEnabled' as const;

export const isPasskeyLoginClientCapable = (): boolean =>
  !isMobileApp() && !isEmbeddedBrowser() && browserSupportsWebAuthn();

export const withPasskeyLoginDecision = <
  TPayload extends Record<string, unknown>,
>(
  payload: TPayload,
  enabled: boolean
): TPayload & Record<typeof PASSKEY_LOGIN_ENABLED_FORM_KEY, boolean> =>
  ({
    ...payload,
    [PASSKEY_LOGIN_ENABLED_FORM_KEY]: enabled,
  }) as TPayload & Record<typeof PASSKEY_LOGIN_ENABLED_FORM_KEY, boolean>;

export interface PasskeyLoginGateDecision {
  enabled: boolean;
}

/**
 * Resolves the pre-auth passkey-login kill switch against the provider's
 * anonymous stable ID. Capability and initialization failures fail closed so
 * the ordinary login journey never waits for or depends on Statsig.
 */
export const usePasskeyLoginGate = (
  currentScreenName: string | undefined
): PasskeyLoginGateDecision => {
  const {
    getFlagFromWarehouseNative,
    getFlagFromWarehouseNativeWithExposure,
    initialised,
  } = useFlagsFromDualInstances();
  const clientCapable = isPasskeyLoginClientCapable();
  const hasLoggedExposureRef = useRef(false);
  const hasReportedGateReadFailureRef = useRef(false);
  const hasRecordedGateReadinessTimingRef = useRef(false);
  const isLoginScreen = currentScreenName === ScreenNames.LOGIN_PAGE;
  let enabled = false;
  let gateReadSucceeded = false;
  let gateReadFailed = false;

  if (clientCapable && initialised) {
    try {
      enabled = Boolean(getFlagFromWarehouseNative(PASSKEY_LOGIN_GATE));
      gateReadSucceeded = true;
    } catch {
      enabled = false;
      gateReadFailed = true;
    }
  }

  useEffect(() => {
    if (
      !isLoginScreen ||
      !clientCapable ||
      hasRecordedGateReadinessTimingRef.current
    ) {
      return;
    }

    hasRecordedGateReadinessTimingRef.current = true;
    addClientTiming(
      initialised
        ? PASSKEY_LOGIN_GATE_READY_TIMING
        : PASSKEY_LOGIN_GATE_NOT_READY_TIMING
    );
  }, [clientCapable, initialised, isLoginScreen]);

  useEffect(() => {
    if (!gateReadFailed || hasReportedGateReadFailureRef.current) {
      return;
    }

    hasReportedGateReadFailureRef.current = true;
    reportClientError(
      'Passkey login gate evaluation failed',
      new Error('Passkey login gate evaluation failed'),
      {
        flow: 'universal-login',
        reason: 'passkey-login-gate-evaluation-failed',
      }
    );
  }, [gateReadFailed]);

  useEffect(() => {
    if (
      !clientCapable ||
      !initialised ||
      !isLoginScreen ||
      !gateReadSucceeded ||
      hasLoggedExposureRef.current
    ) {
      return;
    }

    hasLoggedExposureRef.current = true;
    try {
      getFlagFromWarehouseNativeWithExposure(PASSKEY_LOGIN_GATE);
    } catch {
      reportClientError(
        'Passkey login gate exposure failed',
        new Error('Passkey login gate exposure failed'),
        {
          flow: 'universal-login',
          reason: 'passkey-login-gate-exposure-failed',
        }
      );
    }
  }, [
    clientCapable,
    getFlagFromWarehouseNativeWithExposure,
    initialised,
    isLoginScreen,
    gateReadSucceeded,
  ]);

  return { enabled };
};
